/**
 * SEOListPresenter
 * --
 */
import { useEffect, useReducer, useState } from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  InputFormTable,
} from 'components';
import { Input, Button, Upload } from 'antd';
import { styles } from './styles.js';
import { SettingApi, FileApi } from 'api';
import { UploadOutlined } from '@ant-design/icons';
import MessageAlert from 'utils';

const SEOListPresenter = () => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [ productImages, setProductImages ] = useState([]);
  const [state, dispatch] = useReducer((state, action) => {
    switch(action.type) {
      case 'NAME':
        return {
          ...state,
          name: action.value
        };
      case 'CONTENT':
        return {
          ...state,
          content: action.value
        }
      case 'META_TAG':
        return {
          ...state,
          meta_tag: action.value
        }
      case 'INITIALIZE':
        return action.value
    }
  }, {
    name: '',
    content: '',
    meta_tag: ''
  });
  /* ===== VARIABLE ===== */

  /* ===== FUNCTION ===== */
  const onView = async () => {
    const setting = await SettingApi.getSetting(1);

    if (setting.status === 200) {
      const { seo_setting } = setting.data;

      if (seo_setting) {
        dispatch({ 
          type: 'INITIALIZE', 
          value: { 
            name: seo_setting.title, 
            content: seo_setting.description,
            meta_tag: seo_setting.meta_tag
          } 
        });

        if (seo_setting.image_url) {
          setProductImages([ {
            uid: 'existing', // 임의의 uid (새로 업로드된 파일은 file.uid가 사용됩니다.)
            name: seo_setting.image_url.split('/').pop(), // 파일명 추출 (필요에 따라 수정)
            status: 'done',
            url: `${process.env.REACT_APP_STORE_URL}/${seo_setting.image_url}`,
          }])
        }
      }
    }
  }

  const isValid = () => {
    if (!state.name) {
      MessageAlert.warning('이름을 입력해주세요.');
      return false;
    }

    return true;
  }

  const handleSaveSeoSetting = async () => {
    if (isValid()) {
      try {
        let seo_image_url = null;
        if (productImages.length > 0) {
          const { file, uid } = productImages[0];
          // 공유 이미지 업로드
          const shareImageResponse = await FileApi.uploadFile({
            file: file,
            file_name: uid,
          });
  
          if (shareImageResponse.status === 200) {
            seo_image_url = shareImageResponse.data;
          }
        }
       
        const response = await SettingApi.updateSetting({
          setting_id: 1,
          seo_setting: {
            title: state.name,
            description: state.content,
            meta_tag: state.meta_tag,
            image_url: seo_image_url
          }
        });
  
        if (response && response.status === 200) {
          MessageAlert.success('검색엔진 최적화 설정을 저장하였습니다.');
          onView();
        } else {
          MessageAlert.warning('검색엔진 최적화 설정 저장을 실패하였습니다. 관리자에게 문의하세요.');
        }
      } catch (error) {
        MessageAlert.warning('검색엔진 최적화 설정 저장을 실패하였습니다. 관리자에게 문의하세요.');
      }
    }
  }

  const customRequest = (options) => {
    const { file, onSuccess, onError } = options;
    try {
      const localUrl = URL.createObjectURL(file);
      const fileData = {
        uid: file.uid,
        name: file.name,
        status: 'done',
        url: localUrl,
        file: file
      };
      onSuccess(fileData);
      // 기존 이미지가 있더라도 새 이미지로 대체
      setProductImages([fileData]);
    } catch (error) {
      onError(error);
      MessageAlert.error(error.message);
    }
  };


  const validateThumbnailSize = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        resolve();
      };

      img.onerror = () => {
        reject(new Error('이미지를 로드하는 중 오류가 발생했습니다.'));
      };
    });
  }

  const handleChangeProductImage = (info) => {
    // info.fileList가 2개 이상일 경우 마지막 파일만 남김
    setProductImages(info.fileList.slice(-1));
  };
  /* ===== HOOKS ===== */
  useEffect(() => {
    onView();
  }, [])
  /* ===== RENDER ===== */
  return (
    <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
      <Row>
        <Col x={24}>
          <Title style={{ ...styles.title }}>검색엔진 최적화</Title>

          {/* 정책목록 */}
          <Content maxWidth={'100%'} padding={20} borderRadius={4}>
            <div>
              <InputFormTable 
                form={[
                  {
                    label: '이름',
                    required: true,
                    form: [
                      {
                        input: (
                          <Input 
                            size="large" 
                            placeholder="내용을 입력해주세요" 
                            value={state.name} 
                            onChange={(e) => dispatch({ type: 'NAME', value: e.target.value })} 
                          />
                        ),
                        fullscreen: true,
                        count: true,
                        maxLength: 30,
                      },
                    ]
                  },
                  {
                    label: '안내설명',
                    form: [
                      {
                        input: (
                          <Input.TextArea
                            placeholder="내용을 입력해주세요"
                            value={state.content}
                            style={{ height: 98 }}
                            onChange={(e) => dispatch({ type: 'CONTENT', value: e.target.value })}
                          />
                        ),
                        fullscreen: true,
                        count: true,
                        maxLength: 1000,
                      },
                    ],
                  },
                  {
                    label: '메타태그 추가',
                    direction: 'column',
                    form: [
                      {
                        input: (
                          <Input
                            size="large"
                            placeholder={'<meta name=”site-verification” content=코드 />'}
                            value={state.meta_tag}
                            onChange={(e) => dispatch({ type: 'META_TAG', value: e.target.value })}
                          />
                        ),
                        fullscreen: true,
                      },
                    ],
                  },
                  {
                    label: '공유 이미지 등록',
                    direction: 'column',
                    form: [
                      {
                        input: (
                          <Upload
                            customRequest={(options) => customRequest(options)}
                            listType="picture"
                            maxCount={1}
                            fileList={productImages}
                            onChange={handleChangeProductImage}
                            onRemove={() => setProductImages([])}
                            beforeUpload={(file) => {
                              return validateThumbnailSize(file)
                                .then(() => true)
                                .catch((error) => {
                                  MessageAlert.error(error.message);
                                  return Upload.LIST_IGNORE; // 업로드 중단
                                });
                            }}

                          >
                            <Button type="primary" icon={<UploadOutlined />}>
                              공유 이미지 업로드
                            </Button>
                          </Upload>
                        ),
                        fullscreen: true,
                      },
                    ],
                  },
                ]}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: 26,
                }}
              >
                <Button
                  size="large"
                  style={{
                    ...styles.button,
                    background: 'var(--primary-color)',
                    color: '#FFFFFF',
                  }}
                  onClick={handleSaveSeoSetting}
                >
                  저장
                </Button>
              </div>
            </div>
          </Content>
          {/* 정책목록 끝 */}
        </Col>
      </Row>
    </Content>
  );
};

export default SEOListPresenter;
